import ReactDOMServer from "react-dom/server";
import { batch } from "react-redux";
import moment from "moment";
import type { AnyAction } from "redux";
import { isKeyOf } from "../../../types/internal/utility";
import { SALE_TRANSACTION_FINISHED } from "../../_Bootstrap/GlobalConstants";
import type { ShopfrontDispatch } from "../../_Bootstrap/hooks";
import { history, store } from "../../_Bootstrap/RoutingAndStore";
import type { LocalDatabaseCustomer, LocalDatabaseGiftCard } from "../../_Generated/Sync.gen";
import { CancelOptionButton } from "../../Common/Buttons/Button";
import { TextReplacement } from "../../Common/Forms/DragDrop/InternalComponents/Text/TextReplacement";
import PopupManager from "../../Common/Popups/PopupManager";
import { Receipt } from "../../Common/Receipts/Receipt";
import type { LocalDatabaseMovement } from "../../Data/_Bases/BaseMovementRepository";
import type {
    LocalDatabaseSale,
    LocalDatabaseSaleItem,
    SaleActionType,
    ServerSaleStatus,
} from "../../Data/_Bases/BaseSalesRepository";
import type { UpdateSaleQueuedDate } from "../../Data/_Bases/BaseUploadRepository";
import { determineDataSource, doesDatabaseNeedUpdating } from "../../Data/DataStores/DataSource";
import { calculateTotals, freshSale } from "../../Reducers/SellScreen/SellScreenSaleReducer";
import type {
    SaleCustomer,
    SaleProduct,
    SaleState,
    SaleSurcharge,
    SaleVoucherProduct,
} from "../../Reducers/SellScreen/SellScreenSaleReducerTypes";
import ReprintReceipt from "../../Routes/Register/SalesHistory/ReprintReceipt";
import AddNote from "../../Routes/SellScreen/Dialogs/AddNote";
import AddOrderReference from "../../Routes/SellScreen/Dialogs/AddOrderReference";
import MultipleInvoices from "../../Routes/SellScreen/Dialogs/MultipleInvoices";
import Cashout from "../../Routes/SellScreen/Dialogs/Payments/Cashout";
import ReloadInvoice from "../../Routes/SellScreen/Dialogs/ReloadInvoice";
import RequestEmailReceipt from "../../Routes/SellScreen/Dialogs/RequestEmailReceipt";
import RequestEmailUpdate from "../../Routes/SellScreen/Dialogs/RequestEmailUpdate";
import SafeDropConfirmDialog from "../../Routes/SellScreen/Dialogs/SafeDrop/SafeDropConfirmDialog";
import SafeDropFormDialog from "../../Routes/SellScreen/Dialogs/SafeDrop/SafeDropFormDialog";
import SafeDropRequiredDialog from "../../Routes/SellScreen/Dialogs/SafeDrop/SafeDropRequiredDialog";
import RefundFailedVouchersDialog from "../../Routes/SellScreen/Dialogs/Vouchers/RefundFailedVouchersDialog";
import VoucherActivateMethodDialog from "../../Routes/SellScreen/Dialogs/Vouchers/VoucherActivateMethodDialog";
import VoucherRetryDialog from "../../Routes/SellScreen/Dialogs/Vouchers/VoucherRetryDialog";
import NotFoundException from "../../Routes/SellScreen/Exceptions/Data/NotFoundException";
import { determineQuantityInCasesAndItems } from "../../Utilities/Domain/Products/Inventory";
import { PromotionAgent } from "../../Utilities/Domain/Promotions/PromotionAgent";
import ReceiptHelper, { type AdditionalSaleData } from "../../Utilities/Domain/ReceiptHelper";
import {
    formatIndexedDBProduct,
    getSaleFromServer,
    type GraphQLSale,
    SALE_QUERY,
    transformGraphQLToIndexedDB,
    transformIndexedDBToSaleState,
    transformSaleStateToIndexedDB,
} from "../../Utilities/Domain/Sales/SalesUtilities";
import { Permissions } from "../../Utilities/Domain/Users/Permissions";
import type { ShopfrontThunk } from "../../Utilities/Generic/ActionsHelper";
import { roundTo } from "../../Utilities/Generic/Numbers";
import { formatDate, randomString } from "../../Utilities/Generic/Strings";
import UUID from "../../Utilities/Generic/UUID";
import { ApplicationEventsFromShopfront } from "../../Utilities/Platform/Applications/ApplicationEvents";
import { getApplicationManager } from "../../Utilities/Platform/Applications/ApplicationUtilities";
import { EmbeddedSaleUpdate } from "../../Utilities/Platform/Applications/EmbeddedSaleUpdate";
import { getLogLevel, LogLevel } from "../../Utilities/Platform/Logging";
import {
    deleteOption,
    getOption,
    isTrainingMode,
    setOption,
    setTempOption,
} from "../../Utilities/Platform/Options/Options";
import Request, { CacheRequest } from "../../Utilities/Platform/Request";
import {
    openDrawer,
    sendDockets,
    sendGiftCards,
    sendSale,
} from "../../Workers/HardwareConnect/HardwareConnectEvents";
import { REGISTER_INVOICE_NUMBER_TOUCHED_KEY } from "../../Workers/Sync/SyncEvents";
import { getOpenPages, refreshSale } from "../../Workers/Tab/TabEvents";
import { sendAction, sendSecurityEvent, upload } from "../../Workers/Upload/UploadAgent";
import { UPLOAD_TYPES } from "../../Workers/Upload/UploadEvents";
import { hideDialog, showDialog, type ShowDialogAction } from "../Dialogs/DialogActions";
import { type LayoutActionTypes, startLoading, stopLoading } from "../General/LayoutActions";
import { NoopAction } from "../General/MiscActions";
import { toggleTraining } from "../Information/InformationActions";
import { userLogout } from "../Users/UserActions";
import type { ChangeClassificationAction } from "./SellScreenClassificationActions";
import {
    type ADD_INTEGRATED_PRODUCT_TO_SALE,
    type MARK_PRODUCT_AS_ADDING,
    SALE_ADD_ACTION,
    SALE_ADD_NOTE,
    SALE_ADD_ORDER_REFERENCE,
    SALE_ADD_REFUND_REASON,
    SALE_CHANGE_PRICE_SET,
    SALE_CLEAR,
    SALE_OVERRIDE_META_DATA,
    SALE_PREVENT_CANCELLATION,
    SALE_REQUEST_ASSOCIATE_BARCODE,
    SALE_RETRIEVE,
    type SALE_SET_CURRENTLY_DISCOUNTING,
    SALE_SET_LIVE_PROFIT,
    SALE_SET_SURCHARGING,
    SALE_TOGGLE_DISPLAY_NOTE,
    SALE_UPDATE_PRODUCT,
    SELL_SCREEN_ACTION_MODE_EMBEDDED,
    SELL_SCREEN_ACTIVE_PRODUCT_INDEX,
    SELL_SCREEN_CHANGE_ACTION_MODE,
    SELL_SCREEN_CHANGE_INVALID_INDEXES,
    SELL_SCREEN_CHANGE_RESULTS_TO,
    SELL_SCREEN_CHANGE_SUMMARY_MODE,
    SELL_SCREEN_NEXT_SEARCH_ACTION,
    type SELL_SCREEN_PREVIOUS_ACTION_MODE,
    type SELL_SCREEN_PREVIOUS_SUMMARY_MODE,
    type SELL_SCREEN_PROMOTION_TYPE_CHANGE,
    type SELL_SCREEN_RESET_SEARCH_CARET,
    SELL_SCREEN_SEARCH_ADJUST_INDEX,
    SELL_SCREEN_SEARCH_ADJUST_RESULT,
    SELL_SCREEN_SEARCH_CHANGE,
    SELL_SCREEN_SEARCH_REPLACE,
    SELL_SCREEN_SEARCH_RESULTS_LENGTH,
    SELL_SCREEN_SET_SEARCHING,
    type SELL_SCREEN_TOGGLE_MOBILE_OPTIONS,
    UPDATE_CURRENT_SALE,
    UPDATE_PREVIOUS_SALE,
} from "./SellScreenConstants";
import type { CustomerActionTypes } from "./SellScreenCustomerActions";
import { type PaymentActionTypes, requestRefundReason } from "./SellScreenPaymentActions";
import {
    getSurchargeRules,
    hideProductDetails,
    type SellScreenProductActionTypes,
    sendDeleteProductAction,
} from "./SellScreenProductActions";
import { resetSaleKeys } from "./SellScreenSaleKeysActions";

declare global {
    interface DocumentEventMap {
        [SALE_TRANSACTION_FINISHED]: CustomEvent<SaleState | false>;
    }
}

export type SellScreenActionMode =
    "search" |
    "keys" |
    "held-sales" |
    "table-map" |
    "payment" |
    "customers" |
    "components" |
    "components-search" |
    "component-add" |
    "component-remove" |
    "classifications" |
    "promotions" |
    "search-additional" |
    "show-backorders" |
    "fulfilment-orders" |
    typeof SELL_SCREEN_ACTION_MODE_EMBEDDED;

export type SellScreenNoteToType = "sale" | "internal" | Array<number>;

export type SellScreenSummaryMode = "transaction" | "payments" | "product-details" | "customer-details" | "receipts";

export interface SearchAdditionalType {
    start: false | number;
    end: false | number;
}

export interface ProductUpdateInterface {
    indexAddress: Array<number>;
}

interface ToggleMobileAction {
    type: typeof SELL_SCREEN_TOGGLE_MOBILE_OPTIONS;
}

export interface SearchChangeAction {
    type: typeof SELL_SCREEN_SEARCH_CHANGE;
    value: string;
    additional?: SearchAdditionalType;
}

export interface SearchingSetAction {
    type: typeof SELL_SCREEN_SET_SEARCHING;
    searching: boolean;
}

interface SearchReplaceAction {
    type: typeof SELL_SCREEN_SEARCH_REPLACE;
    value: string;
    caret: false | number;
}

interface ResetSearchCaretAction {
    type: typeof SELL_SCREEN_RESET_SEARCH_CARET;
}

export interface AdjustSearchIndexAction {
    type: typeof SELL_SCREEN_SEARCH_ADJUST_INDEX;
    amount: number | false;
    absolute?: boolean;
}

interface ChangeActionModeAction {
    type: typeof SELL_SCREEN_CHANGE_ACTION_MODE;
    mode: SellScreenActionMode;
    options: Record<string, unknown>;
}

interface ChangeSummaryModeAction {
    type: typeof SELL_SCREEN_CHANGE_SUMMARY_MODE;
    mode: SellScreenSummaryMode;
}

interface PreviousActionModeAction {
    type: typeof SELL_SCREEN_PREVIOUS_ACTION_MODE;
    prevAction: SellScreenActionMode;
}

interface PreviousSummaryModeAction {
    type: typeof SELL_SCREEN_PREVIOUS_SUMMARY_MODE;
    prevSummary: SellScreenSummaryMode;
}

interface SearchResultsLengthAction {
    type: typeof SELL_SCREEN_SEARCH_RESULTS_LENGTH;
    amount: number;
}

interface AdjustSearchResultAction {
    type: typeof SELL_SCREEN_SEARCH_ADJUST_RESULT;
    kind: "product" | "customer" | "gift_card";
    uuid: string | false;
}

interface ActiveProductIndexAction {
    type: typeof SELL_SCREEN_ACTIVE_PRODUCT_INDEX;
    absolute: boolean;
    amount: number;
}

interface ChangeResultToAction {
    type: typeof SELL_SCREEN_CHANGE_RESULTS_TO;
    to: string;
}

interface ChangeInvalidIndexesAction {
    type: typeof SELL_SCREEN_CHANGE_INVALID_INDEXES;
    invalid: Array<number>;
}

interface NextSearchActionAction {
    type: typeof SELL_SCREEN_NEXT_SEARCH_ACTION;
    action: "case" | false;
}

export type PromotionViewType = "current" | "future" | "all";

interface PromotionTypeChangeAction {
    type: typeof SELL_SCREEN_PROMOTION_TYPE_CHANGE;
    mode: PromotionViewType;
}

interface ChangePriceSetAction {
    type: typeof SALE_CHANGE_PRICE_SET;
    priceSet: string;
}

export interface AddNoteAction extends ProductUpdateInterface {
    type: typeof SALE_ADD_NOTE;
    note: string;
    to: SellScreenNoteToType;
}

interface ToggleDisplayNoteAction {
    type: typeof SALE_TOGGLE_DISPLAY_NOTE;
}

interface SetLiveProfitAction {
    type: typeof SALE_SET_LIVE_PROFIT;
    liveProfit: boolean;
}

interface AddOrderReferenceAction {
    type: typeof SALE_ADD_ORDER_REFERENCE;
    reference: string;
}

interface AddRefundReasonAction {
    type: typeof SALE_ADD_REFUND_REASON;
    reason: string;
}

export interface ClearSaleAction {
    type: typeof SALE_CLEAR;
    ignoreBroadcast: boolean;
}

interface RetrieveSaleAction {
    type: typeof SALE_RETRIEVE;
    state: SaleState;
}

export interface GiftCardUsedType {
    code: string;
    original: number;
    amount: number;
    current: number;
    expiry: string;
}

export type PreviousSaleState = (SaleState & {
    printReceipt: boolean;
    printed?: boolean;
    giftCardsUsed: Array<GiftCardUsedType>;
}) | false;

interface UpdatePreviousSaleAction {
    type: typeof UPDATE_PREVIOUS_SALE;
    sale: PreviousSaleState;
}

interface UpdateCurrentSaleAction {
    type: typeof UPDATE_CURRENT_SALE;
    sale: SaleState;
}

export interface MarkProductAsAddingToSale {
    type: typeof MARK_PRODUCT_AS_ADDING;
    id: number | null;
}

export interface AddingIntegratedProductToSale {
    type: typeof ADD_INTEGRATED_PRODUCT_TO_SALE;
    enabled: boolean;
}

export interface UpdateProductAction {
    type: typeof SALE_UPDATE_PRODUCT;
    indexAddress: Array<number>;
    changes: Partial<SaleProduct>;
}

export interface AddSaleAction {
    type: typeof SALE_ADD_ACTION;
    action: SaleActionType;
    timestamp: string;
    value: string;
    affecting: string;
    user: string;
}

export interface SaleSurchargingAction {
    type: typeof SALE_SET_SURCHARGING;
    rules: Array<SaleSurcharge>;
}

interface SaleOverrideMetaDataAction {
    type: typeof SALE_OVERRIDE_META_DATA;
    metaData: Record<string, unknown>;
}

interface SalePreventCancellationAction {
    type: typeof SALE_PREVENT_CANCELLATION;
    isCancellable: boolean;
}

interface SaleSetDiscountingAction {
    type: typeof SALE_SET_CURRENTLY_DISCOUNTING;
    item: string | null;
}

export type SellScreenActionTypes =
    ToggleMobileAction |
    SearchChangeAction |
    SearchingSetAction |
    SearchReplaceAction |
    ResetSearchCaretAction |
    AdjustSearchIndexAction |
    ChangeActionModeAction |
    ChangeSummaryModeAction |
    PreviousActionModeAction |
    PreviousSummaryModeAction |
    SearchResultsLengthAction |
    AdjustSearchResultAction |
    ActiveProductIndexAction |
    ChangeResultToAction |
    ChangeInvalidIndexesAction |
    NextSearchActionAction |
    PromotionTypeChangeAction |
    ChangePriceSetAction |
    AddNoteAction |
    ToggleDisplayNoteAction |
    SetLiveProfitAction |
    AddOrderReferenceAction |
    AddRefundReasonAction |
    ClearSaleAction |
    RetrieveSaleAction |
    UpdatePreviousSaleAction |
    UpdateCurrentSaleAction |
    ChangeClassificationAction |
    LayoutActionTypes |
    SellScreenProductActionTypes |
    ShowDialogAction |
    CustomerActionTypes |
    PaymentActionTypes |
    MarkProductAsAddingToSale |
    AddingIntegratedProductToSale |
    UpdateProductAction |
    AddSaleAction |
    SaleSurchargingAction |
    SaleOverrideMetaDataAction |
    SalePreventCancellationAction |
    SaleSetDiscountingAction;

export function clearSale(ignoreBroadcast = false): SellScreenActionTypes {
    return {
        type: SALE_CLEAR,
        ignoreBroadcast,
    };
}

export function changeSellScreenSearch(
    value: string,
    additional?: SearchAdditionalType
): SellScreenActionTypes {
    return {
        type: SELL_SCREEN_SEARCH_CHANGE,
        additional,
        value,
    };
}

export function setSellScreenSearching(searching: boolean): SearchingSetAction {
    return {
        type: SELL_SCREEN_SET_SEARCHING,
        searching,
    };
}

export function changeSellScreenSearchReplace(value: string, caret: number | false = false): SellScreenActionTypes {
    return {
        type: SELL_SCREEN_SEARCH_REPLACE,
        value,
        caret,
    };
}

export function changeSellScreenSearchResultsTo(to: string): SellScreenActionTypes {
    return {
        type: SELL_SCREEN_CHANGE_RESULTS_TO,
        to,
    };
}

export function setInvalidIndexes(invalid: Array<number>): SellScreenActionTypes {
    return {
        type: SELL_SCREEN_CHANGE_INVALID_INDEXES,
        invalid,
    };
}

/**
 * Adjust the sell screen search index
 * @param amount the amount to adjust by
 * @param absolute Whether the provided amount is relative or absolute
 */
export function adjustSellScreenSearchIndex(amount: number, absolute = false): SellScreenActionTypes {
    return {
        type: SELL_SCREEN_SEARCH_ADJUST_INDEX,
        amount,
        absolute,
    };
}

export function clearSellScreenSearchIndex(): SellScreenActionTypes {
    return {
        type  : SELL_SCREEN_SEARCH_ADJUST_INDEX,
        amount: false,
    };
}

export function clearSellScreenSearch(): ShopfrontThunk<SellScreenActionTypes> {
    return (dispatch): void => {
        batch(() => {
            dispatch(changeSellScreenSearchResultsTo("sale"));
            dispatch(changeSellScreenSearch("[Clear]"));
            dispatch(clearSellScreenSearchIndex());
        });
    };
}

export function changeSellScreenActionMode(
    mode: SellScreenActionMode,
    options: Record<string, unknown> = {}
): ShopfrontThunk<SellScreenActionTypes> {
    return (dispatch): void => {
        batch(() => {
            if(options.clearBarcode) {
                dispatch({
                    type   : SALE_REQUEST_ASSOCIATE_BARCODE,
                    enabled: false,
                    barcode: null,
                });
            }

            dispatch({
                type: SELL_SCREEN_CHANGE_ACTION_MODE,
                mode,
                options,
            });

            dispatch(clearSellScreenSearch());
        });
    };
}

export function showSellScreenComponentAddRemove(remove: boolean): ShopfrontThunk<SellScreenActionTypes> {
    return (dispatch, getState): void => {
        const state = getState();

        const index = state.sellscreen.search.active;
        let validProduct = false;

        if(state.sellscreen.sale.products.length) {
            const product = state.sellscreen.sale.products[index];

            validProduct = product.type === "Package";
        }

        if(validProduct) {
            dispatch(changeSellScreenActionMode(remove ? "component-remove" : "component-add"));
        } else {
            PopupManager.error("Select Package to use Component Add/Remove sale keys");
        }
    };
}

export function changeSellScreenSummaryMode(mode: SellScreenSummaryMode): ShopfrontThunk<SellScreenActionTypes> {
    return (dispatch, getState): void => {
        if(mode === "transaction" && getState().sellscreen.sale.productDetails.index.length) {
            dispatch(hideProductDetails());
        }

        dispatch({
            type: SELL_SCREEN_CHANGE_SUMMARY_MODE,
            mode,
        });
    };
}

export function changeSellScreenResultsLength(amount: number): SellScreenActionTypes {
    return {
        type: SELL_SCREEN_SEARCH_RESULTS_LENGTH,
        amount,
    };
}

export function changeSelectedSearchResult(
    kind: "product" | "customer" | "gift_card",
    uuid: string | false
): SellScreenActionTypes {
    return {
        type: SELL_SCREEN_SEARCH_ADJUST_RESULT,
        kind,
        uuid,
    };
}

export function changeActiveProductIndex(amount: number, absolute = false): ShopfrontThunk<SellScreenActionTypes> {
    return (dispatch, getState): void => {
        const state = getState();

        if(!absolute) {
            if(state.sellscreen.search.active + amount < 0) {
                absolute = true;
                amount = 0;
            } else if(state.sellscreen.search.active + amount >= state.sellscreen.sale.products.length) {
                absolute = true;
                amount = state.sellscreen.sale.products.length - 1;
            }
        }

        if(amount < 0 && absolute) {
            amount = 0;
        }

        dispatch({
            type: SELL_SCREEN_ACTIVE_PRODUCT_INDEX,
            amount,
            absolute,
        });
    };
}

export function toggleDisplayNotes(): SellScreenActionTypes {
    return {
        type: SALE_TOGGLE_DISPLAY_NOTE,
    };
}

export function showAddNote(
    to: SellScreenNoteToType,
    note = "",
    callback?: () => void
): ShopfrontThunk<SellScreenActionTypes | ShowDialogAction> {
    return (dispatch): void => {
        let label;

        if(to === "internal") {
            label = "Add Internal Note";
        } else if(to === "sale") {
            label = "Add Note to Sale";
        } else {
            label = "Add Note to Product";
        }

        dispatch(changeSellScreenSummaryMode("transaction"));
        dispatch(showDialog(
            label,
            <AddNote to={to} note={note} callback={callback} />,
            "question",
            false,
            false
        ));
    };
}

export function addNoteToSale(to: SellScreenNoteToType, note: string): SellScreenActionTypes {
    return {
        type        : SALE_ADD_NOTE,
        indexAddress: [],
        note,
        to,
    };
}

export function openOrderReferenceDialog(): ShopfrontThunk<SellScreenActionTypes> {
    return (dispatch, getState): void => {
        const state = getState();

        return dispatch(showDialog(
            "Modify Order Reference",
            <AddOrderReference reference={state.sellscreen.sale.orderReference} />,
            "question",
            false,
            false
        ));
    };
}

export function addOrderReference(reference: string): SellScreenActionTypes {
    return {
        type: SALE_ADD_ORDER_REFERENCE,
        reference,
    };
}

export function overrideSaleMetaData(metaData: Record<string, unknown>): SellScreenActionTypes {
    return {
        type: SALE_OVERRIDE_META_DATA,
        metaData,
    };
}

export function preventSaleFromBeingCancelled(preventCancellation = true): SellScreenActionTypes {
    return {
        type         : SALE_PREVENT_CANCELLATION,
        isCancellable: !preventCancellation,
    };
}

export function addRefundReason(reason: string): SellScreenActionTypes {
    return {
        type: SALE_ADD_REFUND_REASON,
        reason,
    };
}

export function changeSalePriceSet(priceSet: string): SellScreenActionTypes {
    return {
        type: SALE_CHANGE_PRICE_SET,
        priceSet,
    };
}

export function updateProduct(indexAddress: Array<number>, changes: Partial<SaleProduct>): UpdateProductAction {
    return {
        type: SALE_UPDATE_PRODUCT,
        indexAddress,
        changes,
    };
}

export function showReloadInvoiceDialog(): ShopfrontThunk<SellScreenActionTypes | ShowDialogAction> {
    return (dispatch): void => {
        dispatch(startLoading());

        Permissions.can("refund")
            .then(permitted => {
                dispatch(stopLoading());

                if(permitted) {
                    dispatch(showDialog(
                        "Reload Invoice",
                        <ReloadInvoice />,
                        "question",
                        false,
                        false
                    ));
                } else {
                    PopupManager.error("You do not have permission to reload invoices");
                }
            })
            .catch(err => {
                dispatch(stopLoading());

                if(getLogLevel() >= LogLevel.NORMAL) {
                    console.error("Error showing reload invoice dialog", err);
                }

                PopupManager.error("You do not have permission to reload invoices");
            });
    };
}

/**
 * Adjust the takings for a register
 */
export async function adjustTakings(amount: number, method: string): Promise<void> {
    const source = await determineDataSource();
    let taking;

    try {
        taking = await source.takings.get(method);
    } catch(e) {
        if(e instanceof NotFoundException) {
            const paymentMethod = await source.paymentMethods.get(method);

            taking = {
                uuid  : method,
                type  : paymentMethod.type,
                amount: 0,
            };
        } else {
            throw e;
        }
    }

    console.log(`Adjusting the taking amount for ${taking.uuid} from $${taking.amount} by $${amount} (sale)`);
    deleteOption(`close_register_${taking.uuid}`);

    return source.takings.put({
        ...taking,
        amount: taking.amount + amount,
    })
        .then(() => {
            // Do nothing, just to return void
        });
}

export async function unparkSale(uuid: string, cancel = false): Promise<LocalDatabaseSale> {
    if(process.env.NODE_ENV !== "test" && getLogLevel() >= LogLevel.NORMAL) {
        console.log("Unparking sale with ID", uuid);
    }

    const source = await determineDataSource();
    let sale;

    try {
        sale = await source.sales.get(uuid);

        // Remove the gift cards
        for(let i = 0, l = sale.items.length; i < l; i++) {
            const item = sale.items[i];

            if(item.item.type === "GiftCard") {
                try {
                    const giftCard = await source.giftCards.get(item.item.id);

                    giftCard.status = "cancelled";
                    await source.giftCards.put(giftCard);
                } catch {
                    // Do nothing
                }
            }
        }

        if(cancel) {
            const transformed = await transformIndexedDBToSaleState(sale);

            for(let i = 0, l = transformed.products.length; i < l; i++) {
                sendDeleteProductAction(transformed.products[i]);
            }
        }

        // Update the status of the local sale
        sale.status = "UNPARKED";
        await source.sales.put(sale);
    } catch(err) {
        if(!(err instanceof NotFoundException)) {
            console.error("Received error while unparking a sale", err);
            throw err;
        }

        if(uuid.includes("-")) {
            try {
                const sales = await source.sales.getByClientId(uuid);

                for(let i = 0, l = sales.length; i < l; i++) {
                    if(sales[i].status === "PARKED") {
                        sale = sales[i];
                        break;
                    }
                }
            } catch(err) {
                if(!(err instanceof NotFoundException)) {
                    console.error("Received error while unparking a local sale", err);
                    throw err;
                }
            }
        }
    }

    if(!sale) {
        throw new NotFoundException();
    }

    // Check to see if the sale is offline
    if(sale.id.includes("-")) {
        // Remove all uploads that relate to this sale (this sale won't exist on the server)
        try {
            const uploads = await source.uploads.all();

            let clientId = sale.clientId;

            if(!clientId) {
                clientId = sale.id;
            }

            const toRemove: Array<number> = [];

            for(const upload of uploads) {
                if(typeof upload.id !== "number") {
                    continue;
                }

                if(upload.type !== UPLOAD_TYPES.UPDATE_SALE) {
                    continue;
                }

                const data = upload.data as UpdateSaleQueuedDate;

                if(data.clientId !== clientId || data.invoiceId !== sale.invoiceId) {
                    continue;
                }

                toRemove.push(upload.id);
            }

            await source.uploads.delete(toRemove);
        } catch(err) {
            console.error("Error removing offline sale updates", err);
        }
    }

    // Get the current register
    const currentRegister = getOption("register"); // Would use the global state, but might be dangerous?

    void sendSecurityEvent("UNPARKED_SALE", {
        id      : uuid,
        clientId: sale.clientId,
        linkedTo: sale.linkedTo || undefined,
    });

    // Upload unpark sale
    await upload(UPLOAD_TYPES.UNPARK_SALE, {
        id      : uuid,
        time    : moment().toISOString(true),
        register: currentRegister,
    });

    // Adjust the local takings
    if(!cancel) {
        for(let i = 0, l = sale.payments.length; i < l; i++) {
            if(sale.payments[i].status !== "completed") {
                continue;
            }

            try {
                await adjustTakings(sale.payments[i].amount * -1, sale.payments[i].paymentMethodId);
            } catch {
                // Just ignore, it's likely to be loyalty, but it could be a deleted payment method
            }
        }
    }

    // Clear sale history cache
    if(typeof CacheRequest.clearCache === "function") {
        CacheRequest.clearCache();
    }

    return sale;
}

export function reloadInvoice(
    sale: LocalDatabaseSale,
    popup = true,
    toSellScreen = false,
    invert = true,
    includeNotes = false
): ShopfrontThunk<SellScreenActionTypes> {
    return async (dispatch): Promise<void> => {
        if(invert) {
            const permitted = await Permissions.can("refund");

            if(!permitted) {
                PopupManager.error("You do not have permission to refund a sale");

                return;
            }
        }

        let state: SaleState;

        let suffix = "";

        if(sale.status === "PARKED" || sale.status === "UNPARKED") {
            state = await transformIndexedDBToSaleState(sale, true);
            delete state.createdAt;
        } else {
            state = freshSale();

            if(sale.id) {
                state.linkedTo = sale.id;
            }

            if(includeNotes) {
                state.orderReference = sale.orderReference;
            }

            let giftCards = false;

            const products: Array<Promise<SaleProduct>> = [];

            for(let i = 0; i < sale.items.length; i++) {
                const product = sale.items[i];

                if(product.quantity === 0) {
                    continue;
                }

                if(product.item.type === "GiftCard") {
                    giftCards = true;
                    continue;
                }

                if(product.item.type === "Surcharge") {
                    continue;
                }

                product.quantity = product.quantity * (invert ? -1 : 1);
                product.totalPrice = product.totalPrice * (invert ? -1 : 1);
                // Base price should always be positive
                product.basePrice = Math.abs(product.totalPrice / product.quantity);

                if(!includeNotes) {
                    product.note = "";
                }

                products.push(formatIndexedDBProduct(
                    product,
                    true,
                    true,
                    () => {
                        giftCards = true;
                    },
                    (invert || !!product.discount.amount)
                ));
            }

            state.products = await Promise.all(products);

            if(sale.customerId) {
                state.customer = {
                    uuid: sale.customerId,
                };
            }

            if(includeNotes) {
                state.notes = sale.notes;
            }

            if(giftCards) {
                suffix = ". However, gift cards cannot be returned";
            }
        }

        if(popup) {
            PopupManager.info("Invoice reloaded successfully" + suffix);
        }

        if(process.env.NODE_ENV !== "test") {
            console.log("Overriding the sale due to a call to `reloadInvoice`");
        }

        let surchargeRules: Array<SaleSurcharge> = [];

        if(!invert) {
            surchargeRules = await getSurchargeRules();
        }

        batch(() => {
            dispatch({
                type: SALE_RETRIEVE,
                state,
            });
            dispatch(setSaleSurchargingRules(surchargeRules));
            dispatch(changeSellScreenActionMode("keys"));
            dispatch(changeSellScreenSummaryMode("transaction"));
            dispatch(hideDialog());
        });

        const pages = await getOpenPages();

        if(pages["/"]) {
            PopupManager.info("Invoice reloaded successfully, Sell screen is already open");
            console.log("Triggering tab sale refresh due to call to `reloadInvoice`");
            refreshSale();
        } else {
            if(toSellScreen) {
                history.push("/");
            }
        }
    };
}

function showMultipleInvoicesFound(sales: Array<LocalDatabaseSale>): ShopfrontThunk<ShowDialogAction> {
    return showDialog(
        "Multiple Invoices",
        <MultipleInvoices results={sales} />,
        "question",
        [ CancelOptionButton ],
        false,
        false
    );
}

function searchInvoicesOnline(invoiceId: string): ShopfrontThunk<SellScreenActionTypes> {
    if(invoiceId.charAt(0) === "R") {
        invoiceId = invoiceId.slice(1);
    }

    return (dispatch): void => {
        dispatch(startLoading());

        Request.get<{
            data: {
                sales: {
                    edges: Array<{
                        node: GraphQLSale;
                    }>;
                };
            };
        }>("/graphql", {
            params: {
                query: `
                query SearchInvoice($invoiceId: String) {
                    sales(invoiceId: $invoiceId) {
                        edges {
                            node {
                                ${SALE_QUERY}
                            }
                        }
                    }
                }
            `,
                variables: {
                    invoiceId: invoiceId,
                },
            },
        })
            .then(response => {
                const data = response.data.data.sales.edges;

                if(!data.length) {
                    PopupManager.warning("Could not find sales");
                } else {
                    const sales: Array<LocalDatabaseSale> = [];

                    for(let i = 0; i < data.length; i++) {
                        sales.push(transformGraphQLToIndexedDB(data[i].node));
                    }

                    if(sales.length === 1) {
                        if(sales[0].status === "PARKED") {
                            unparkSale(sales[0].id)
                                .then(() => {
                                    dispatch(reloadInvoice(sales[0]));
                                });
                        } else {
                            dispatch(reloadInvoice(sales[0]));
                        }
                    } else {
                        dispatch(showMultipleInvoicesFound(sales));
                    }
                }
            })
            .finally(() => {
                dispatch(stopLoading());
            });
    };
}

export function searchInvoices(invoiceId: string): ShopfrontThunk<SellScreenActionTypes> {
    if(invoiceId.charAt(0) === "R") {
        invoiceId = invoiceId.slice(1);
    }

    return async (dispatch): Promise<void> => {
        dispatch(startLoading());

        if(!await Permissions.can("refund")) {
            PopupManager.error("You do not have permission to reload invoices");
            dispatch(stopLoading());
            throw new Error("Not permitted");
        }

        const source = await determineDataSource();

        let finishLoading = true;
        const sales = await source.sales.search(invoiceId);

        if(sales.length === 0) {
            dispatch(searchInvoicesOnline(invoiceId));
            finishLoading = false;
        } else {
            if(sales.length === 1) {
                if(sales[0].status === "PARKED") {
                    await unparkSale(sales[0].id);
                }

                dispatch(reloadInvoice(sales[0]));
            } else {
                dispatch(showMultipleInvoicesFound(sales));
            }
        }

        if(finishLoading) {
            dispatch(stopLoading());
        }
    };
}

export function loadInvoiceFromUuid(
    uuid: string,
    toSellScreen = false,
    invert = true,
    includeNotes = false
): ShopfrontThunk<SellScreenActionTypes> {
    return (dispatch): void => {
        dispatch(startLoading());

        Request.get<{
            data: {
                sale: GraphQLSale;
            };
        }>("/graphql", {
            params: {
                query: `query LoadSale($id: ID!) {
                    sale(id: $id) {
                        ${SALE_QUERY}
                    }
                }`,
                variables: {
                    id: uuid,
                },
            },
        })
            .then(response => {
                const data = response.data.data.sale;
                const sale = transformGraphQLToIndexedDB(data, invert);

                dispatch(reloadInvoice(sale, true, toSellScreen, invert, includeNotes));
            })
            .finally(() => {
                dispatch(stopLoading());
            });
    };
}

function pad(value: string, width: number, padding: string): string {
    const paddingLength = Math.min(width - value.length, 20);
    let pad = "";

    if(paddingLength > 0) {
        pad = padding.repeat(paddingLength);
    }

    return pad + value;
}

/**
 * Generates an invoice id
 */
export function generateInvoiceId(offline: boolean): string {
    const register = getOption("register");

    if(!register) {
        console.warn("Invoice is being generated when no register has been selected");
    }

    // Get suffix
    let suffix = "";

    if(isTrainingMode()) {
        suffix = "TM";
    } else if(offline) {
        suffix = getOption("register_offline_invoice_suffix");
    }

    const invoiceWidth = getOption("invoice_width");
    const remainingWidth = Math.max(invoiceWidth - suffix.length, 1);

    const generationMethod = getOption("register_invoice_number_mode");

    let invoiceNumber: string;

    if(generationMethod === "random_string") {
        invoiceNumber = randomString(remainingWidth);
    } else if(generationMethod === "random_number") {
        invoiceNumber = Math.round(Math.random() * (Math.pow(10, remainingWidth) - 2) + 1).toString();
    } else {
        let current = getOption("register_current_invoice_number");
        const max = getOption("register_current_invoice_max");

        if(typeof max === "number") {
            if(current >= max) {
                current = getOption("register_next_invoice_batch") ?? 0;

                setOption("register_next_invoice_batch", null);
                setOption("register_current_invoice_max", null);
            }
        }

        setOption("register_current_invoice_number", ++current);

        // We've established this number ourselves this session, so a sync shouldn't silently replace it
        setTempOption(REGISTER_INVOICE_NUMBER_TOUCHED_KEY, true);

        if(register) {
            determineDataSource()
                .then(source => {
                    return source.registers.get(register)
                        .then(dbRegister => {
                            return source.registers.update(register, {
                                settings: {
                                    ...dbRegister.settings,
                                    invoice_number: current,
                                },
                            });
                        });
                })
                .catch(err => {
                    console.error("Error when trying to update the current invoice number", err);
                });
        }

        invoiceNumber = current.toString();
    }

    return pad(invoiceNumber + suffix, invoiceWidth, "0");
}

export function checkIfSaleIsComplete(
    total: number,
    remaining: number,
    paid: number,
    refund = false,
    overpayment = false
): boolean {
    if(remaining === 0) {
        return true;
    }

    return overpayment && !refund && total < paid;
}

export type SaleFinishOptions = {
    vouchers: "auto" | Array<SaleVoucherProduct>;
    voucherFailed: boolean; // Used when voucher payment failed
    voucherCancel: boolean; // Used when voucher payments are canceled rather than retried after a voucher payment fails
    skipLogoutCheck: boolean;
};

function showActivateVoucher(vouchers: Array<SaleVoucherProduct>): ReturnType<typeof showDialog> {
    return showDialog(
        "Activate Voucher",
        <VoucherActivateMethodDialog vouchers={vouchers} />,
        "information",
        false,
        false,
        false,
        false
    );
}

function showRefundVouchers(
    amount: number,
    removeVouchers: Array<number>,
    vouchers: Array<SaleVoucherProduct>
): ReturnType<typeof showDialog> {
    return showDialog(
        "Refund Vouchers",
        <RefundFailedVouchersDialog amount={amount} removeVouchers={removeVouchers} vouchers={vouchers} />,
        "question",
        false,
        false,
        false,
        false
    );
}

function showRetryVouchers(options: SaleFinishOptions): ReturnType<typeof showDialog> {
    return showDialog(
        "Retry Vouchers",
        <VoucherRetryDialog options={options} />,
        "question",
        false,
        false,
        false,
        false
    );
}

function handleVouchers(sale: SaleState, options: SaleFinishOptions): ShopfrontThunk<SellScreenActionTypes> {
    return (dispatch): void => {
        if(options.vouchers === "auto") {
            options.vouchers = [];

            // Prepare vouchers
            for(let i = 0; i < sale.products.length; i++) {
                const product = sale.products[i];

                if(product.type === "Voucher") {
                    options.vouchers.push({
                        ...product,
                        index: i,
                    });
                }
            }
        }

        // Check if vouchers need to be processed
        if(options.vouchers.length) {
            // Check if the last voucher has failed
            if(options.voucherFailed) {
                if(options.voucherCancel) {
                    // Prepare to remove the remaining vouchers from sale
                    const removeIndexes = [];

                    // Calculate total that needs refunding
                    let refundTotal = 0;
                    const vouchers = [];

                    while(options.vouchers.length) {
                        const voucher = options.vouchers.pop();

                        if(!voucher) {
                            break;
                        }

                        removeIndexes.push(voucher.index);
                        refundTotal += voucher.prices.price;
                        vouchers.push(voucher);
                    }

                    // Show refund amount
                    dispatch(showRefundVouchers(refundTotal, removeIndexes, vouchers));
                } else {
                    // Ask if payment should be retried
                    dispatch(showRetryVouchers(options));
                }
            } else {
                // Process next voucher
                dispatch(showActivateVoucher(options.vouchers));
            }
        } else if(typeof sale.status !== "undefined") {
            dispatch(finishSale(sale.status, options) as ShopfrontThunk);
        } else {
            console.error("Invalid options given to voucher handler");
        }
    };
}

function requestUpdateCustomerEmail(options: SaleEmailReceiptOptions): ShopfrontThunk<SellScreenActionTypes> {
    return (dispatch): void => {
        dispatch(showDialog(
            "Update Email",
            <RequestEmailUpdate options={options} />,
            "question",
            false,
            false,
            true,
            true,
            true,
            true
        ));
    };
}

export type SaleEmailReceiptOptions = {
    sale: "auto" | SaleState;
    email: string;
    defaultMail: string;
    customer: boolean | string | "auto";
    receipt: string | null;
    overLogin: boolean;
    additionalSaleData?: AdditionalSaleData;
};

export function sendEmailReceipt(
    passedOptions?: Partial<SaleEmailReceiptOptions>
): ShopfrontThunk<SellScreenActionTypes | ShowDialogAction> {
    const defaultOptions: SaleEmailReceiptOptions = {
        sale       : "auto",
        customer   : "auto",
        email      : "auto",
        defaultMail: "",
        receipt    : "auto",
        overLogin  : false,
    };

    const options = {
        ...defaultOptions,
        ...passedOptions,
    };

    return async (dispatch, getState): Promise<void> => {
        const state = getState();

        if(options.sale === "auto") {
            if(state.sellscreen.previousSale === false) {
                throw new Error("Previous sale state is false");
            }

            options.sale = state.sellscreen.previousSale;
        }

        if(options.customer === "auto") {
            let customer: false | string | SaleCustomer = options.sale.customer;

            if(customer) {
                customer = customer.uuid;
            }

            options.customer = customer;
        }

        const source = await determineDataSource();
        let customer;

        if(typeof options.customer !== "boolean") {
            try {
                customer = await source.customers.get(options.customer);
            } catch {
                // Do nothing
            }
        }

        if(options.email === "auto") {
            options.email = "";

            if(customer) {
                options.email = customer.billing.email;
                options.defaultMail = customer.billing.email;
            }

            return dispatch(showDialog(
                "Email",
                <RequestEmailReceipt options={options} />,
                "question",
                false,
                false,
                true,
                true,
                true,
                options.overLogin
            ));
        }

        const data = await ReceiptHelper.createReceiptDataFromSaleState(
            options.sale as Required<SaleState>,
            options.additionalSaleData
        );

        if(options.receipt === "auto") {
            if(customer?.customer_group_id) {
                try {
                    const customerGroup = await source.customerGroups.get(
                        customer.customer_group_id
                    );

                    if(customerGroup.email_receipt_id) {
                        options.receipt = customerGroup.email_receipt_id;
                    }
                } catch {
                    // Do nothing
                }
            }

            try {
                if(
                    options.receipt === "auto" &&
                    options.sale.registerId !== false
                ) {
                    const register = await source.registers.get(options.sale.registerId);
                    const outlet = await source.outlets.get(register.outlet_id);

                    if(outlet.settings.email_receipt_template) {
                        options.receipt = outlet.settings.email_receipt_template as string | null;
                    }
                }
            } catch {
                // Ignore
            }

            if(options.receipt === "auto") {
                options.receipt = getOption("outlet_email_receipt_template");
            }
        }

        if(!options.receipt) {
            PopupManager.error("There is no email receipt template available");

            return;
        }

        let receipt;

        try {
            receipt = await source.receipts.get(options.receipt);
        } catch {
            const emailTemplate = getOption("outlet_email_receipt_template");

            if(!emailTemplate) {
                PopupManager.error(
                    "Unable to find the receipt template, and no default receipt template specified in the " +
                    "settings."
                );

                return;
            } else if(emailTemplate === options.receipt) {
                PopupManager.error("Unable to find the default receipt template, check the outlet's settings.");

                return;
            }

            try {
                receipt = await source.receipts.get(emailTemplate);
            } catch {
                PopupManager.error("Unable to find the default receipt template, check the outlet's settings.");
                // Do nothing
            }
        }

        if(!receipt) {
            return;
        }

        try {
            const components = JSON.parse(receipt.components);

            const receiptDOM = ReactDOMServer.renderToStaticMarkup(
                <Receipt
                    padding={receipt.padding}
                    type={receipt.type}
                    data={data as Record<string, any>}
                    format={components}
                    width={receipt.receiptWidth}
                    attachment
                />
            );

            const attachments = [];

            if(receipt.attachments) {
                for(let i = 0, l = receipt.attachments.length; i < l; i++) {
                    try {
                        const attachmentReceipt = await source.receipts.get(receipt.attachments[i].receipt);

                        attachments.push({
                            name: TextReplacement.parse(data.text as Record<string, any>, receipt.attachments[i].name),
                            data: ReactDOMServer.renderToStaticMarkup(
                                <Receipt
                                    padding={attachmentReceipt.padding}
                                    type={attachmentReceipt.type}
                                    data={data as Record<string, any>}
                                    format={JSON.parse(attachmentReceipt.components)}
                                    width={attachmentReceipt.receiptWidth}
                                    attachment
                                />
                            ),
                        });
                    } catch {
                        console.error("Unable to generate attachment for receipt");
                    }
                }
            }

            await upload(UPLOAD_TYPES.EMAIL_RECEIPT, {
                to       : options.email,
                register : options.sale.registerId,
                receipt  : receiptDOM,
                status   : options.sale.status,
                invoiceNo: options.sale.invoiceId,
                attachments,
            });

            PopupManager.success("Email queued for sending");
        } catch(e) {
            console.error("Unable to generate email receipt", e);
            PopupManager.error("We were unable to generate and send the email receipt");
        }

        if(options.customer && options.email !== options.defaultMail) {
            dispatch(requestUpdateCustomerEmail(options));
        }
    };
}

function showCashoutAndChange(
    cashout: Array<{
        method?: string;
        amount: number;
        name?: string;
    }>,
    hasChange = false,
    hasRefund = false
): ReturnType<typeof showDialog> {
    let title;

    if(hasRefund && cashout.length === 1) {
        title = "Refund";
    } else if(hasChange && cashout.length === 1) {
        title = "Change";
    } else if(hasChange && hasRefund && cashout.length === 2) {
        title = "Refund and Change";
    } else {
        title = "Cash Out";

        if(hasChange) {
            title = `${title} and Change`;
        }

        if(hasRefund) {
            title = `${title} and Refund`;
        }
    }

    return showDialog(
        title,
        <Cashout cashout={cashout} hasChange={hasChange} hasRefund={hasRefund} />,
        "information",
        false,
        false,
        false,
        true,
        true,
        true
    );
}

function showSafeDropRequired(): ReturnType<typeof showDialog> {
    return showDialog(
        "Safe Drop Required",
        <SafeDropRequiredDialog />,
        "information",
        false,
        false
    );
}

export function prepareSale(): ShopfrontThunk<SellScreenActionTypes> {
    return async (dispatch, getState): Promise<void> => {
        dispatch(resetSaleKeys());

        const state = getState();

        if(state.user.id === false) {
            return;
        }

        if(history.location.pathname !== "/") {
            return;
        }

        if(state.dialog.visible) {
            return;
        }

        if(!await Permissions.can("perform_safe_drop")) {
            return;
        }

        if(isTrainingMode()) {
            return;
        }

        const safeDropLimit = getOption("register_safe_drop_amount");

        if(safeDropLimit > 0) {
            const source = await determineDataSource();
            const takings = await source.takings.getByType("cash");

            let cashInDrawer = 0;

            for(let i = 0; i < takings.length; i++) {
                cashInDrawer += takings[i].amount;
            }

            if(cashInDrawer >= safeDropLimit) {
                dispatch(showSafeDropRequired());
            }
        }
    };
}

export async function updateTakings(takings: Array<{ method: string; amount: number; }>): Promise<void> {
    for(let i = 0, l = takings.length; i < l; i++) {
        await adjustTakings(takings[i].amount, takings[i].method);
    }
}

export function showTrainingModeActive(): ShopfrontThunk<SellScreenActionTypes> {
    return (dispatch): void => {
        dispatch(showDialog(
            "Training Mode",
            <div style={{ maxWidth: "350px" }}>
                <p>Training Mode is currently enabled so the previous sale was not recorded</p>
                <p>Do you wish to continue using training mode?</p>
            </div>,
            "question",
            [{
                label   : "No",
                classes : [ "button-cancel" ],
                callback: (): void => dispatch(toggleTraining()),
            }, {
                label  : "Yes",
                classes: [ "button-save" ],
            }]
        ));
    };
}

async function updateProductInventory(saleItems: Array<LocalDatabaseSaleItem>, register: string): Promise<void> {
    const source = await determineDataSource();

    let reg;

    try {
        reg = await source.registers.get(register);
    } catch {
        return;
    }

    if(!reg) {
        return;
    }

    let outlet;

    try {
        outlet = await source.outlets.get(reg.outlet_id);
    } catch {
        return;
    }

    if(!outlet) {
        return;
    }

    outlet = outlet.uuid;

    for(let i = 0, l = saleItems.length; i < l; i++) {
        const saleItem = saleItems[i];

        if(saleItem.item.type !== "Product") {
            continue;
        }

        try {
            const quantity = saleItem.quantity;
            const product = await source.products.get(saleItem.item.id);

            const inventory = product.inventory;

            for(let j = 0, n = inventory.length; j < n; j++) {
                if(inventory[j].outlet_id === outlet) {
                    let cases = inventory[j].case_level;
                    let items = inventory[j].single_level;

                    if(isNaN(cases)) {
                        cases = 0;
                    }

                    if(isNaN(items)) {
                        items = 0;
                    }

                    let inv = cases * product.case_quantity + items;

                    inv -= quantity;

                    const determined = determineQuantityInCasesAndItems(inv, product.case_quantity);

                    inventory[j] = {
                        ...inventory[j],
                        outlet_id   : outlet,
                        case_level  : determined.cases,
                        single_level: determined.items,
                    };

                    break;
                }
            }

            await source.products.put({
                ...product,
                inventory,
            });
        } catch {
            // Just ignore, the sync should fix it up
        }
    }
}

let finishingSale = false;
let finishSaleAttempts = 0;

/**
 * Check if a sale is currently in the process of finishing
 */
export const isSaleFinishing = (): boolean => {
    return finishingSale;
};

interface PreFinishSalePipelineContext {
    user: string | false;
    register: string; // Can be an empty string
}

/**
 * Execute anything that requires a "complete" sale, but before it actually completes
 */
const executePreFinishSalePipeline = async (
    state: SaleState,
    context: PreFinishSalePipelineContext,
    dispatch: ShopfrontDispatch
): Promise<SaleState | false> => {
    let hasDisplayedLoading = false;
    const id = setTimeout(() => {
        hasDisplayedLoading = true;
        dispatch(startLoading());
    }, 1000);

    EmbeddedSaleUpdate.setWithinPreFinishSalePipeline(true);

    const result = await getApplicationManager().sendPipeline<SaleState | false, PreFinishSalePipelineContext>(
        ApplicationEventsFromShopfront.SALE_PRE_FINISH_PIPELINE,
        state,
        context,
        {
            noDataTimeoutPerPipe      : Infinity,
            earlyReturnValue          : false,
            ignoreNotReadyApplications: true,
        }
    );

    EmbeddedSaleUpdate.setWithinPreFinishSalePipeline(false);

    clearTimeout(id);

    if(hasDisplayedLoading) {
        dispatch(stopLoading());
    }

    return result;
};

/**
 * Complete the sale
 * @alias completeSale
 * @alias finaliseSale
 * @alias finalizeSale
 */
export function finishSale(
    status: ServerSaleStatus,
    passedOptions?: Partial<SaleFinishOptions>,
    saleState?: SaleState
): ShopfrontThunk<SellScreenActionTypes> | AnyAction {
    if(process.env.NODE_ENV !== "test") {
        console.log("Finish sale has been initiated", status, passedOptions, saleState);
    }

    if(isSaleFinishing()) {
        console.log("Finish is already in progress, stopping");

        if(finishSaleAttempts > 3) {
            console.log("Finish sale has been attempted more than 3 times while in progress, showing user error");
            PopupManager.info("Finishing the sale, please wait for a moment");
        }

        return NoopAction;
    }

    if(doesDatabaseNeedUpdating()) {
        PopupManager.info("Shopfront requires an update, please refresh the page before the sale will complete");

        return NoopAction;
    }

    finishingSale = true;
    finishSaleAttempts = 0;

    // Ensure that the promotion calculation isn't running
    if(PromotionAgent.getAgent().isCurrentlyCalculating()) {
        PopupManager.info(
            "Promotion calculation is currently running, please wait until it completes to get the correct price"
        );
        console.log("Promotion calculation is running, aborting");

        finishingSale = false;

        return NoopAction;
    }

    const defaultOptions: SaleFinishOptions = {
        vouchers       : "auto",
        voucherFailed  : false,
        voucherCancel  : false,
        skipLogoutCheck: false,
    };

    const options = Object.assign({}, defaultOptions, passedOptions);

    return async (dispatch, getState): Promise<void> => {
        let state = getState();

        if(status === "COMPLETED") {
            await requestRefundReason(dispatch, state.sellscreen.sale, true);

            state = getState();
        }

        let sale: SaleState;

        if(!saleState) {
            sale = state.sellscreen.sale;
        } else {
            sale = calculateTotals(saleState);
        }

        const register = state.location.register?.id ?? "";
        let user = state.user.id;

        if(user === false) {
            user = "11e6d61b3dec425286c45cf9dd713698";
        }

        if(register === "") {
            console.error("There was no register defined when finishing a sale");
        }

        if(status) {
            sale.status = status;
        }

        if(sale.status === "COMPLETED" && (options.vouchers === "auto" || options.vouchers.length > 0)) {
            finishingSale = false;
            dispatch(handleVouchers(sale, options));

            return;
        }

        if(sale.status === "COMPLETED" && options.vouchers.length === 0) {
            const usedIndexes: Array<number> = [];

            for(let i = 0, l = sale.products.length; i < l; i++) {
                if(sale.products[i].type === "Voucher") {
                    // Add the opposite product
                    sale.products.push({
                        ...sale.products[i],
                        quantity: sale.products[i].quantity * -1,
                        prices  : {
                            ...sale.products[i].prices,
                            price: sale.products[i].prices.price * -1,
                        },
                    });

                    sale.totals.sale -= sale.products[i].prices.price;
                    sale.totals.remaining -= sale.products[i].prices.price;

                    // Create a matching action
                    for(let j = 0, n = sale.actions.length; j < n; j++) {
                        if(sale.actions[j].action !== "PRODUCT_SCAN") {
                            continue;
                        }

                        if(sale.actions[j].affecting !== sale.products[i].uuid) {
                            continue;
                        }

                        if(usedIndexes.includes(j)) {
                            continue;
                        }

                        usedIndexes.push(j);
                        sale.actions.push({
                            timestamp: moment().toISOString(),
                            action   : "PRODUCT_SCAN",
                            value    : sale.actions[j].value,
                            affecting: sale.actions[j].affecting,
                            user     : sale.actions[j].user,
                        });
                    }
                }
            }
        }

        let linkedSale: string | false = false;

        if(!sale.id) {
            if(sale.internalId) {
                sale.id = sale.internalId;
            } else {
                sale.id = UUID.generate();
            }
        } else {
            linkedSale = sale.id;
            sale.id = UUID.generate();
            sale.linkedTo = linkedSale;
        }

        if(!sale.invoiceId) {
            sale.invoiceId = generateInvoiceId(!state.information.online);
        }

        if(!sale.createdAt) {
            sale.createdAt = moment().toISOString();
        }

        const salePipelineResult = await executePreFinishSalePipeline(
            sale,
            {
                user,
                register,
            },
            dispatch
        );

        if(salePipelineResult === false) {
            finishingSale = false;

            return;
        } else {
            sale = salePipelineResult;
        }

        const s = transformSaleStateToIndexedDB(sale as Required<SaleState>, {
            userId    : user,
            registerId: register,
        });

        const source = await determineDataSource();

        // Prepare for gift cards
        const giftCards: Array<LocalDatabaseGiftCard> = [];
        const gcPayments: Record<string, number> = {};
        const giftCardsUsed: Record<string, {
            code: string;
            amount: number;
            current: number;
            original: number;
            expiry: string;
        }> = {};

        // Payment Methods
        let paymentMethods: Array<string> = [];
        const paymentMethodKeys: Record<string, boolean> = {};

        // Prepare cash out dialog
        const cashout: Array<{
            method?: string;
            amount: number;
            name?: string;
        }> = [];

        let printReceipt = false;
        let emailReceipt: false | string = false;

        let openCashDrawer = false;
        let hasRefund = false;

        if(status !== "CANCELLED") {
            let loyaltyUsed = 0;
            let accountUsed = 0;

            // Get gift cards used for sales
            for(let i = 0; i < sale.payments.length; i++) {
                const payment = sale.payments[i];

                if(payment.reverted) {
                    continue;
                }

                if(payment.status === "completed" && !paymentMethodKeys[payment.method]) {
                    paymentMethodKeys[payment.method] = true;
                }

                if(payment.cashout && !payment.uploaded && payment.status === "completed") {
                    cashout.push({
                        method: payment.method,
                        amount: payment.cashout,
                    });
                } else if(
                    !payment.cashout &&
                    payment.amount < 0 &&
                    payment.method !== "account" &&
                    payment.voucherRefund !== true &&
                    payment.status === "completed"
                ) {
                    hasRefund = true;
                    cashout.push({
                        method: payment.method,
                        amount: payment.amount,
                        name  : "Refund",
                    });
                }

                let metadata: any = {};

                if(payment.metadata) {
                    metadata = JSON.parse(payment.metadata);
                }

                if(payment.type === "giftcard" && metadata.gift_card.source === "internal") {
                    if(!isKeyOf(gcPayments, metadata.gift_card.id)) {
                        gcPayments[metadata.gift_card.id] = 0;
                    }

                    gcPayments[metadata.gift_card.id] += payment.amount;

                    if(!isKeyOf(giftCardsUsed, metadata.gift_card.id)) {
                        giftCardsUsed[metadata.gift_card.id] = {
                            code    : metadata.gift_card.code,
                            amount  : payment.amount,
                            // current_amount is gift card balance before sale
                            original: metadata.gift_card.current_amount,
                            current : 0, // Will be calculated later
                            expiry  : formatDate(metadata.gift_card.expiry),
                        };
                    } else {
                        giftCardsUsed[metadata.gift_card.id].amount += giftCardsUsed[metadata.gift_card.id].amount;
                    }
                }

                if(payment.method === "account") {
                    accountUsed += payment.amount;
                }

                if(payment.method === "loyalty") {
                    if(metadata.loyalty_points) {
                        loyaltyUsed += metadata.loyalty_points;
                    }
                }
            }

            // Get payment keys
            paymentMethods = Object.keys(paymentMethodKeys);

            // Check if user requires email receipt or print receipt
            if(sale.customer && !isTrainingMode()) {
                try {
                    const customer = await source.customers.get((sale.customer as SaleCustomer).uuid);

                    printReceipt = (
                        status !== "PARKED" && customer.autoprint_receipt
                    ) || (
                        status === "PARKED" && customer.autoprint_parked_receipt
                    );

                    if(customer.customer_group_id) {
                        try {
                            const group = await source.customerGroups.get(
                                customer.customer_group_id
                            );

                            if(group.auto_email_receipt) {
                                emailReceipt = customer.email;
                            }
                        } catch {
                            // Do nothing
                        }
                    }

                    if(status === "COMPLETED") {
                        let update: null | Partial<LocalDatabaseCustomer> = null;

                        if(loyaltyUsed || s.loyaltyValue) {
                            let current = customer.loyalty_points;

                            if(isNaN(current)) {
                                current = 0;
                            }

                            update = {
                                loyalty_points: Math.round(
                                    (current - loyaltyUsed + s.loyaltyValue) * 100
                                ) / 100,
                            };
                        }

                        if(accountUsed) {
                            if(!update) {
                                update = {};
                            }

                            let current = customer.current_owing;

                            if(isNaN(current)) {
                                current = 0;
                            }

                            update.current_owing = Math.round((current + accountUsed) * 100) / 100;
                        }

                        if(update) {
                            await source.customers.update(customer.uuid, update);
                        }
                    }
                } catch(e) {
                    console.error("We were unable to update the customer locally", e);
                }
            }

            // Check if receipt needs to be printed and drawer needs to be opened
            for(let i = 0; i < paymentMethods.length; i++) {
                try {
                    const method = await source.paymentMethods.get(paymentMethods[i]);

                    if(method.always_print) {
                        printReceipt = true;
                    }

                    if(method.always_open_drawer) {
                        openCashDrawer = true;
                    }

                    for(let i = 0; i < cashout.length; i++) {
                        if(cashout[i].method === method.uuid) {
                            delete cashout[i].method;

                            if(typeof cashout[i].name === "string") {
                                cashout[i].name = `${method.name} (${cashout[i].name})`;
                            } else {
                                cashout[i].name = method.name;
                            }
                        }
                    }
                } catch {
                    // Do nothing
                }
            }
        }

        if(status === "COMPLETED") {
            // Calculate change
            if(sale.totals.remaining < 0) {
                // Get the round to options
                const amountToRoundTo = getOption("round_to");
                let decimalLength: Array<string> | number = amountToRoundTo.toString().split(".");

                if(decimalLength.length > 1) {
                    decimalLength = Math.pow(10, decimalLength[1].length);
                } else {
                    decimalLength = 1;
                }

                // Round total price and change
                s.totalPrice = roundTo(sale.totals.sale, amountToRoundTo);

                // Find the total rounding
                s.totalRounding = Math.round(
                    s.totalPrice * decimalLength - sale.totals.sale * decimalLength
                ) / decimalLength;

                // Calculate total change
                s.totalChange = roundTo(sale.totals.remaining + s.totalRounding, amountToRoundTo) * -1;

                // Add the rounding to the first completed payment (preferably cash though)
                let match = null;

                for(let i = 0, l = s.payments.length; i < l; i++) {
                    if(s.payments[i].status !== "completed") {
                        continue;
                    }

                    if(match === null) {
                        match = i;
                    }

                    try {
                        const method = await source.paymentMethods.get(s.payments[i].paymentMethodId);

                        if(method.type === "cash") {
                            match = i;
                            break;
                        }
                    } catch {
                        // Do nothing
                    }
                }

                if(match !== null) {
                    s.payments[match].rounding += s.totalRounding;
                }
            }
        }

        if(status !== "CANCELLED") {
            // Pull the gift cards from the sale and store them
            for(let i = 0; i < sale.products.length; i++) {
                const product = sale.products[i];

                if(product.giftCard) {
                    if(product.giftCard.source !== "internal") {
                        // Enterprise gift cards are already reserved
                        continue;
                    }

                    giftCards.push({
                        uuid           : product.uuid,
                        client_id      : "",
                        code           : product.giftCard.code,
                        status         : status === "PARKED" ? "pending" : "active",
                        current_amount : product.giftCard.amount,
                        expiry         : product.giftCard.expiry,
                        original_amount: product.giftCard.amount,
                        outlet_id      : state.location.outlet?.id ?? "", // Invalid but there will be an Outlet
                    });

                    giftCardsUsed[product.uuid] = {
                        code    : product.giftCard.code,
                        current : product.giftCard.amount,
                        original: product.giftCard.amount,
                        amount  : 0,
                        expiry  : formatDate(product.giftCard.expiry),
                    };
                }
            }
        }

        let hasChange = false;

        if(s.totalChange) {
            hasChange = true;
            cashout.push({
                name  : "Change",
                amount: s.totalChange,
            });
        }

        if(!isTrainingMode()) {
            // Update the gift card values
            for(const uuid in gcPayments) {
                if(!isKeyOf(gcPayments, uuid)) {
                    continue;
                }

                const amount = gcPayments[uuid];

                if(amount === 0) {
                    continue;
                }

                try {
                    const giftCard = await source.giftCards.get(uuid);

                    giftCard.current_amount = (Math.round(
                        giftCard.current_amount * 100 - amount * 100
                    ) / 100);

                    giftCards.push(giftCard);
                } catch(e) {
                    console.error("Unable to retrieve gift card for updating", e);
                }
            }

            // Update the gift cards in the local file and send them across the network
            if(giftCards.length) {
                try {
                    await source.giftCards.put(giftCards);
                    sendGiftCards(giftCards);
                } catch(e) {
                    console.error("Unable to update or send gift cards", e);
                }
            }

            // Remove the linked sale and add the sale to the device
            try {
                if(linkedSale) {
                    await source.sales.delete(linkedSale);
                }

                await source.sales.put(s);

                // Send to other registers on other registers
                if(status === "COMPLETED" || status === "PARKED") {
                    sendSale(s);
                }
            } catch(err) {
                // NOTE: This is a big problem if we get this error, we might need to do something more.
                console.error("Unable to update the sale in the local device", err);
            }
        }

        // Calculate the current amount of the gift card
        for(const card in giftCardsUsed) {
            if(!Object.prototype.hasOwnProperty.call(giftCardsUsed, card)) {
                continue;
            }

            giftCardsUsed[card].current = Math.round(
                (giftCardsUsed[card].original - giftCardsUsed[card].amount) * 100
            ) / 100;
        }

        const completedSale = {
            ...sale,
            printReceipt : printReceipt,
            giftCardsUsed: Object.values(giftCardsUsed),
            status       : status,
            userId       : user,
            registerId   : register,
            invoiceId    : s.invoiceId,
            totals       : {
                ...sale.totals,
                change  : s.totalChange,
                rounding: s.totalRounding,
            },
        };

        if(emailReceipt && !isTrainingMode() && status === "COMPLETED") {
            dispatch(sendEmailReceipt({
                sale       : completedSale,
                email      : emailReceipt,
                defaultMail: emailReceipt,
                customer   : (sale.customer as SaleCustomer).uuid,
            }));
        }

        await new Promise<void>(res => {
            let updateCalled = false;
            const update = (): void => {
                if(updateCalled) {
                    return;
                }

                updateCalled = true;

                if(getOption("register_print_receipt_on_refund") && s.totalPrice < 0) {
                    completedSale.printReceipt = true;
                }

                dispatch({
                    type: UPDATE_PREVIOUS_SALE,
                    sale: completedSale,
                });

                res();
            };

            if(openCashDrawer && !isTrainingMode() && !getOption("register_never_open_cash_drawer")) {
                const updateTimeout = setTimeout(() => {
                    update();
                }, 500);

                openDrawer({
                    register,
                    from         : "Sale",
                    requestReason: false,
                    callback     : (reason, error) => {
                        clearTimeout(updateTimeout);

                        if(error) {
                            completedSale.printReceipt = true;
                        }

                        update();
                    },
                });
            } else {
                update();
            }
        });

        try {
            if(s.status === "PARKED") {
                void sendSecurityEvent("PARKED_SALE", {
                    id      : s.id,
                    clientId: s.clientId,
                    linkedTo: s.linkedTo || undefined,
                });
            }

            // Upload the sale to the server
            upload(UPLOAD_TYPES.SALE, s.id)
                .then(uploaded => {
                    const freshState = getState();

                    if((freshState.information.online || uploaded) && state.user.id !== false && state.user.offline) {
                        if(!options.skipLogoutCheck) {
                            if(getLogLevel() === LogLevel.VERBOSE) {
                                console.log("Logging out due to sale finishing and currently offline user");
                            }

                            dispatch(userLogout(true));
                        }
                    }
                });

            let showedCashoutAndChange = false;

            if(s.status === "COMPLETED" || s.status === "PARKED") {
                dispatch(changeSellScreenActionMode("keys"));
                dispatch(changeSellScreenSummaryMode("receipts"));

                if(cashout.length) {
                    if(cashout.length > 1 || cashout[0].name !== "Change") {
                        showedCashoutAndChange = true;
                        dispatch(showCashoutAndChange(cashout, hasChange, hasRefund));
                    }
                }
            } else {
                dispatch(changeSellScreenActionMode("keys"));
            }

            if(getOption("register_login_after_sale")) {
                if(!options.skipLogoutCheck) {
                    if(getLogLevel() === LogLevel.VERBOSE) {
                        console.log("Logging out due to sale finishing and setting requires logout");
                    }

                    dispatch(userLogout());
                }
            }

            if(!isTrainingMode() && s.status !== "CANCELLED") {
                void sendDockets(sale, user, s.status);
            }

            dispatch(clearSale());
            dispatch(prepareSale());

            if(typeof document !== "undefined") {
                document.dispatchEvent(new CustomEvent(SALE_TRANSACTION_FINISHED, {
                    detail: sale,
                }));
            }

            const updateSaleTakings = async (): Promise<void> => {
                // Update the takings
                const takings: Record<string, number> = {};

                for(let i = 0, l = s.payments.length; i < l; i++) {
                    if(
                        s.payments[i].paymentMethodId === "account" ||
                        s.payments[i].paymentMethodId === "loyalty"
                    ) {
                        continue;
                    }

                    if(s.payments[i].status !== "completed") {
                        continue;
                    }

                    if(typeof takings[s.payments[i].paymentMethodId] === "undefined") {
                        takings[s.payments[i].paymentMethodId] = 0;
                    }

                    takings[s.payments[i].paymentMethodId] += s.payments[i].amount;

                    if(s.payments[i].cashout) {
                        const paymentMethod = await source.paymentMethods.get(
                            s.payments[i].paymentMethodId
                        );

                        const cashoutMethodId = paymentMethod.cashout_method_id ?? paymentMethod.uuid;

                        if(typeof takings[cashoutMethodId] === "undefined") {
                            takings[cashoutMethodId] = 0;
                        }

                        takings[s.payments[i].paymentMethodId] += s.payments[i].cashout;
                        takings[cashoutMethodId] -= s.payments[i].cashout;
                    }
                }

                // Remove the change if required
                if(s.totalChange !== 0) {
                    const changeMethod = await source.paymentMethods.getByType("cash");

                    if(typeof takings[changeMethod[0].uuid] === "undefined") {
                        takings[changeMethod[0].uuid] = 0;
                    }

                    takings[changeMethod[0].uuid] -= s.totalChange;
                }

                const takingsToUpdate = [];

                for(const i in takings) {
                    if(!Object.prototype.hasOwnProperty.call(takings, i)) continue;

                    takingsToUpdate.push({
                        method: i,
                        amount: takings[i],
                    });
                }

                return updateTakings(takingsToUpdate);
            };

            if(isTrainingMode()) {
                if(!showedCashoutAndChange) {
                    dispatch(showTrainingModeActive());
                }
            } else if(s.status === "COMPLETED") {
                // Update the inventory for the products
                await updateProductInventory(s.items, s.registerId);
                await updateSaleTakings();
            } else if(s.status === "PARKED") {
                await updateSaleTakings();
            }

            finishingSale = false;

            if(process.env.NODE_ENV !== "test") {
                console.log("Sale has been finished");
            }
        } catch(e) {
            console.error("Error while finishing the sale", e);
            finishingSale = false;
        }
    };
}

export function cancelSale(): ShopfrontThunk<SellScreenActionTypes> {
    console.log("Attempting to cancel the current sale");

    return (dispatch, getState): void => {
        // Get the state
        const state = getState();

        // Get products and register
        const products = state.sellscreen.sale.products;
        const paid = state.sellscreen.sale.totals.paid;
        const register = state.location.register?.id ?? "";

        if(paid !== 0) {
            PopupManager.error("Please reverse each payment before cancelling the sale");
            dispatch(changeSellScreenActionMode("payment"));
            dispatch(changeSellScreenSummaryMode("payments"));

            return;
        }

        batch(() => {
            // Send the cancelled sale to the server
            dispatch(finishSale("CANCELLED") as ShopfrontThunk);

            // Clear sale from sell screen
            dispatch(changeSellScreenActionMode("keys"));
            dispatch(changeSellScreenSummaryMode("transaction"));
            dispatch(clearSale());
        });

        // Send all products deleted from sale
        for(let i = 0, l = products.length; i < l; i++) {
            const product = products[i];

            sendDeleteProductAction(product);
        }

        // Send cancelled sale action to server
        void sendAction("CANCELLED_SALE", "App\\Models\\Register", register);
        void sendSecurityEvent("CANCELLED_SALE", {});
    };
}

export function showReprintReceipt(
    id: string,
    additionalSaleData?: AdditionalSaleData
): ShopfrontThunk<SellScreenActionTypes | ShowDialogAction> {
    return (dispatch): void => {
        dispatch(startLoading());

        getSaleFromServer(id)
            .catch(err => {
                console.error("Error trying to get sale from server", err);

                return determineDataSource()
                    .then(source => {
                        return source.sales.get(id);
                    });
            })
            .then(sale => transformIndexedDBToSaleState(sale, true, false, true))
            .then(sale => {
                for(let i = 0, l = sale.products.length; i < l; i++) {
                    if(
                        sale.products[i].canUnlock &&
                        sale.products[i].prices.normal !== sale.products[i].prices.price &&
                        Object.keys(sale.products[i].promotions).length === 0
                    ) {
                        sale.products[i].prices.unlockedPrice = sale.products[i].prices.normal;
                        sale.products[i].edited = true;
                    }
                }

                dispatch(stopLoading());
                dispatch(showDialog(
                    "Reprint Receipt",
                    <ReprintReceipt
                        sale={sale}
                        additionalSaleData={additionalSaleData}
                    />,
                    "question",
                    false,
                    false,
                    true,
                    false
                ));
            })
            .catch(err => {
                PopupManager.error("Failed to load sale for reprinting receipt");
                console.error("Error loading reprint receipt", err);
                dispatch(stopLoading());
            });
    };
}

export function sendHistoricEmailReceipt(
    id: string,
    additionalSaleData?: AdditionalSaleData
): ShopfrontThunk<SellScreenActionTypes> {
    return (dispatch): void => {
        dispatch(startLoading());

        getSaleFromServer(id)
            .catch(err => {
                console.error("Error trying to get sale from server", err);

                return determineDataSource()
                    .then(source => {
                        return source.sales.get(id);
                    });
            })
            .then(sale => transformIndexedDBToSaleState(sale, false, true, true))
            .then(sale => {
                dispatch(stopLoading());
                dispatch(sendEmailReceipt({
                    sale: sale,
                    additionalSaleData,
                }));
            })
            .catch(err => {
                PopupManager.error("Failed to load sale for email receipt");
                console.error("Error loading historic email receipt", err);
                dispatch(stopLoading());
            });
    };
}

export function showSafeDropForm(callback?: () => void): ReturnType<typeof showDialog> {
    return showDialog(
        "Safe Drop",
        <SafeDropFormDialog callback={callback} />,
        "question",
        false,
        false
    );
}

export function showSafeDropConfirm(
    amount: number,
    method: string,
    callback?: () => void
): ReturnType<typeof showDialog> {
    return showDialog(
        "Confirm Safe Drop",
        <SafeDropConfirmDialog amount={amount} method={method} callback={callback} />,
        "question",
        false,
        false
    );
}

/**
 * Perform a safe drop
 */
export function safeDrop(amount: number, method: string, callback?: () => void): ShopfrontThunk<SellScreenActionTypes> {
    return async (dispatch, getState): Promise<void> => {
        const state = getState();

        if(state.user.id === false) {
            PopupManager.error("There doesn't appear to be a user logged in.");

            return;
        }

        if(!state.location.register?.id) {
            PopupManager.error("There doesn't seem to be a register in use at the moment");

            return;
        }

        const movement: LocalDatabaseMovement = {
            uuid         : UUID.generate(),
            amount       : (amount * -1).toString(),
            reason       : "Safe Drop",
            user         : state.user.id,
            register     : state.location.register.id,
            paymentMethod: method,
            timestamp    : moment().toISOString(),
            denominations: [],
        };

        const source = await determineDataSource();

        await source.movements.put(movement);

        await adjustTakings(amount * -1, method);

        if(typeof callback === "function") {
            callback();
        }

        await upload(UPLOAD_TYPES.TAKINGS, {
            ...movement,
            denominations: {},
        });

        PopupManager.success("Safe drop completed");
    };
}

export function toggleLiveProfit(): ShopfrontThunk<SellScreenActionTypes> {
    return (dispatch, getState): void => {
        const state = getState();

        Permissions.can("see_cost")
            .then(can => {
                if(can) {
                    const value = !state.sellscreen.miscellaneous.liveProfit;

                    setOption("live_profit", value);

                    dispatch({
                        type      : SALE_SET_LIVE_PROFIT,
                        liveProfit: value,
                    });
                }
            });
    };
}

export function setLiveProfit(liveProfit: boolean): ShopfrontThunk<SellScreenActionTypes> {
    return (dispatch): void => {
        Permissions.can("see_cost")
            .then(can => {
                if(can) {
                    dispatch({
                        type: SALE_SET_LIVE_PROFIT,
                        liveProfit,
                    });
                }
            });
    };
}

export function setNextSearchAction(action: "case" | false): SellScreenActionTypes {
    return {
        type: SELL_SCREEN_NEXT_SEARCH_ACTION,
        action,
    };
}

export function addSaleAction(
    action: SaleActionType,
    value: string,
    affecting: string
): AddSaleAction {
    const user = store.getState().user.id || "11e6d61b3dec425286c45cf9dd713698";

    return {
        type     : SALE_ADD_ACTION,
        timestamp: moment().toISOString(),
        action,
        value,
        affecting,
        user,
    };
}

export function setSaleSurchargingRules(rules: Array<SaleSurcharge>): SaleSurchargingAction {
    return {
        type: SALE_SET_SURCHARGING,
        rules,
    };
}

export function updateCurrentSale(sale: SaleState): SellScreenActionTypes {
    return {
        type: UPDATE_CURRENT_SALE,
        sale,
    };
}

export const actionModeToSummaryMode: Partial<Record<SellScreenActionMode, SellScreenSummaryMode>> = {
    "payment": "payments",
};

export function isSellScreenActionMode(mode: unknown): mode is SellScreenActionMode {
    if(typeof mode !== "string") {
        return false;
    }

    const actionModes: Array<string> = [
        "search",
        "keys",
        "held-sales",
        "table-map",
        "payment",
        "customers",
        "components",
        "components-search",
        "component-add",
        "component-remove",
        "classifications",
        "promotions",
        "search-additional",
        "show-backorders",
        "fulfilment-orders",
        SELL_SCREEN_ACTION_MODE_EMBEDDED,
    ];

    return actionModes.includes(mode);
}

export function isSellScreenSummaryMode(mode: unknown): mode is SellScreenSummaryMode {
    if(typeof mode !== "string") {
        return false;
    }

    const summaryModes: Array<string> = [
        "transaction",
        "payments",
        "product-details",
        "customer-details",
        "receipts",
    ];

    return summaryModes.includes(mode);
}
